<template>
  <div class="hris-page">
    <div class="hris-page-inner">
    <div class="hris-page-header">
      <span class="hris-page-eyebrow">Timekeeping</span>
      <h1 class="hris-page-title">Calendar</h1>
      <p class="hris-page-subtitle">
        View national and company holidays for the selected year.
      </p>
    </div>

    <div class="hris-toolbar">
      <div class="legend row items-center no-wrap">
        <span class="legend-dot legend-dot--public"></span>
        <span class="legend-label">Public (national)</span>
      </div>
      <div class="legend row items-center no-wrap">
        <span class="legend-dot legend-dot--company"></span>
        <span class="legend-label">Company</span>
      </div>
      <div class="hris-toolbar-spacer"></div>
      <q-select
        v-model="selectedYear"
        :options="yearOptions"
        emit-value
        map-options
        dense
        outlined
        color="green"
        label="Year"
        class="year-select"
      ></q-select>
    </div>

    <div class="calendar-body">
      <div class="hris-panel calendar-panel">
        <div
          v-if="loading"
          class="vc-shrink hris-loading calendar-loading"
        >
          <q-spinner color="green" size="40px" ></q-spinner>
          <span>Loading holidays…</span>
        </div>
        <vc-calendar
          v-else
          :key="'ph-' + selectedYear + '-' + holidaysRevision"
          :attributes="attributes"
          :from-page="calendarFromPage"
          class="vc-shrink"
        />
      </div>

      <div class="hris-panel upcoming-panel">
        <h2 class="hris-form-section-title">Upcoming holidays {{ selectedYear }}</h2>
        <p class="hris-form-section-hint">National and company holidays</p>

        <div v-if="loading" class="hris-loading">
          <q-spinner color="green" size="32px" ></q-spinner>
          <span>Loading…</span>
        </div>
        <div
          v-else-if="upcomingHolidays.length === 0"
          class="hris-empty"
        >
          {{ upcomingEmptyCopy }}
        </div>

        <q-list
          v-else
          bordered
          separator
          class="rounded-borders overflow-hidden"
        >
          <q-item v-for="item in upcomingHolidays" :key="item.rowKey" v-ripple>
            <q-item-section>
              <q-item-label class="item-holiday-name">{{
                item.holiday
              }}</q-item-label>
              <q-item-label caption class="item-holiday-date">{{
                formatDate(item.holiday_date)
              }}</q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

const NAGER_BASE = "https://date.nager.at/api/v3/PublicHolidays";
const PUBLIC_HOLIDAY_COLOR = "#dc3545";
/** Distinct from national highlight so overlapping dates are visible */
const COMPANY_HOLIDAY_COLOR = "#0d9488";

export default {
  name: "Calendar",

  data() {
    return {
      holidays: [],
      selectedYear: new Date().getFullYear(),
      loading: false,
      /** Bump after each successful load so vc-calendar remounts if props alone don't repaint */
      holidaysRevision: 0,
      fetchSeq: 0,
    };
  },

  computed: {
    yearOptions() {
      const cy = new Date().getFullYear();
      const options = [];
      for (let y = cy - 5; y <= cy + 5; y++) {
        options.push({ label: String(y), value: y });
      }
      return options;
    },

    /** v-calendar pane: January of chosen year unless it is current year — then current month */
    calendarFromPage() {
      const now = new Date();
      const y = Number(this.selectedYear);
      const month = y === now.getFullYear() ? now.getMonth() + 1 : 1;
      return { month, year: y };
    },

    upcomingEmptyCopy() {
      const cy = new Date().getFullYear();
      if (Number(this.selectedYear) < cy) {
        return "All holidays for this year are in the past. Choose the current year or a future year to see an upcoming list.";
      }
      if (Number(this.selectedYear) > cy) {
        return "No holidays in the API response for this year yet, or the list is empty.";
      }
      return (
        "No holidays on or after today for " + String(this.selectedYear) + "."
      );
    },

    upcomingHolidays() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startOfSelYear = new Date(Number(this.selectedYear), 0, 1);
      const endOfYear = new Date(Number(this.selectedYear), 11, 31);

      const upcoming = this.holidays.filter((h) => {
        const d = this.parseHolidayDate(h.holiday_date);
        if (Number(this.selectedYear) > today.getFullYear()) {
          return d >= startOfSelYear && d <= endOfYear;
        }
        return d >= today && d <= endOfYear;
      });

      return upcoming.sort((a, b) => {
        const da = this.parseHolidayDate(a.holiday_date);
        const db = this.parseHolidayDate(b.holiday_date);
        return da - db;
      });
    },

    attributes() {
      return [
        ...this.holidays.map((holiday) => {
          const fill =
            holiday.source === "company"
              ? COMPANY_HOLIDAY_COLOR
              : PUBLIC_HOLIDAY_COLOR;
          const popLabel =
            holiday.source === "company" &&
            holiday.locations &&
            String(holiday.locations).trim()
              ? `${holiday.holiday} · ${holiday.locations}`
              : holiday.holiday;
          return {
            key: `cal-${holiday.rowKey}`,
            dates: toIsoDateKey(holiday.holiday_date),
            highlight: {
              fillMode: "solid",
              style: {
                backgroundColor: holiday.color || fill,
                borderRadius: "8px",
              },
              contentStyle: {
                color: "#ffffff",
                fontWeight: "700",
              },
            },
            popover: {
              label: popLabel,
            },
            customData: {
              englishName: holiday.holiday,
              localName: holiday.local_name,
              source: holiday.source,
            },
            /** Company highlights stack above national when the same date has both */
            order: holiday.source === "company" ? 2 : 1,
          };
        }),
      ];
    },
  },

  watch: {
    selectedYear() {
      this.loadCalendarHolidays();
    },
  },

  created() {
    this.loadCalendarHolidays();
  },

  methods: {
    parseHolidayDate(dateStr) {
      const key = toIsoDateKey(dateStr);
      const parts = key.split("-").map(Number);
      const year = parts[0];
      const month = parts[1];
      const day = parts[2];
      if (!year || !month || !day) {
        return new Date(NaN);
      }
      return new Date(year, month - 1, day);
    },

    async loadCalendarHolidays() {
      const year = Number(this.selectedYear);
      this.fetchSeq += 1;
      const seq = this.fetchSeq;
      this.loading = true;
      this.holidays = [];

      let nagerFailed = false;
      let companyFailed = false;

      const publicPromise = axios
        .get(`${NAGER_BASE}/${year}/PH`)
        .then((res) => {
          const rows = Array.isArray(res.data) ? res.data : [];
          return rows.map((h, idx) => ({
            rowKey: `pub-${h.date}-${idx}`,
            holiday:
              h.name && String(h.name).trim()
                ? h.name
                : h.localName || "Holiday",
            holiday_date: toIsoDateKey(h.date),
            color: PUBLIC_HOLIDAY_COLOR,
            local_name: h.localName || "",
            locations: "",
            source: "public",
          }));
        })
        .catch(() => {
          nagerFailed = true;
          return [];
        });

      const api = this.apiUrl;
      const auth = this.token;
      const companyPromise = axios
        .create({
          baseURL: api,
          headers: { Authorization: `Bearer ${auth}` },
        })
        .get(`/timekeeping/holiday/${year}`)
        .then((res) => {
          const list = Array.isArray(res.data && res.data.data)
            ? res.data.data
            : [];
          return list.map((r) => ({
            rowKey: `co-${r.id}`,
            holiday: r.holiday,
            holiday_date: toIsoDateKey(r.holiday_date),
            color: COMPANY_HOLIDAY_COLOR,
            local_name: "",
            locations: r.locations != null ? String(r.locations) : "",
            source: "company",
          }));
        })
        .catch(() => {
          companyFailed = true;
          return [];
        });

      try {
        const [publicRows, companyRows] = await Promise.all([
          publicPromise,
          companyPromise,
        ]);
        if (seq !== this.fetchSeq) {
          return;
        }
        const merged = [...companyRows, ...publicRows];
        merged.sort((a, b) => {
          const da = toIsoDateKey(a.holiday_date);
          const db = toIsoDateKey(b.holiday_date);
          const c = da.localeCompare(db);
          if (c !== 0) {
            return c;
          }
          return String(a.holiday).localeCompare(String(b.holiday));
        });
        this.holidays = merged;
        this.holidaysRevision += 1;

        if (this.$q && typeof this.$q.notify === "function") {
          if (nagerFailed && companyFailed) {
            this.$q.notify({
              type: "negative",
              message:
                "Could not load holidays. Check your connection or sign in again.",
            });
          } else if (nagerFailed) {
            this.$q.notify({
              type: "warning",
              message:
                "National holidays (date.nager.at) unavailable; company holidays may still show.",
            });
          } else if (companyFailed) {
            this.$q.notify({
              type: "warning",
              message:
                "Could not load company holidays from HRIS; national holidays may still show.",
            });
          }
        }
      } finally {
        if (seq === this.fetchSeq) {
          this.loading = false;
        }
      }
    },

    formatDate(dateStr) {
      const options = { year: "numeric", month: "long", day: "numeric" };
      const d = this.parseHolidayDate(dateStr);
      return d.toLocaleDateString(undefined, options);
    },
  },
};

function toIsoDateKey(raw) {
  if (raw == null) {
    return "";
  }
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    return s.slice(0, 10);
  }
  if (/^\d{4}\/\d{2}\/\d{2}/.test(s)) {
    return s.replace(/\//g, "-");
  }
  return s;
}
</script>

<style scoped>
.legend-dot {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  flex-shrink: 0;
}

.legend-dot--public {
  background: #dc3545;
}

.legend-dot--company {
  background: #0d9488;
}

.legend-label {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--hris-text-secondary, #5c6370);
  margin-left: 8px;
}

.year-select {
  min-width: 120px;
}

.calendar-body {
  width: 100%;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 16px;
}

.calendar-panel {
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 280px;
}

.calendar-loading {
  min-height: 340px;
  min-width: 280px;
}

.vc-shrink {
  flex-shrink: 0;
}

.upcoming-panel {
  width: 80%;
  flex-shrink: 0;
  min-width: 280px;
}

.item-holiday-name {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--hris-text, #1a1f2e);
}

.item-holiday-date {
  font-weight: 500;
  color: var(--hris-text-muted, #8b929e);
}

@media (max-width: 600px) {
  .upcoming-panel {
    width: 100%;
  }
}
</style>
